import { Link, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { getPrivacyPolicyLink, getTermsLink } from 'utils/localization'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { FC, useMemo } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUICheckbox } from 'components/common/MUICheckBox'
import i18n from 'translations/i18n'

const linkStyle = { color: GRAY_900, textDecorationColor: GRAY_900 }

/**
 * @component
 * GDPR/T&C consent checkbox shown in the embed order summary while consent is missing (ISSUE-2017).
 * Ticking it is what re-enables the final "Zustimmen und bestellen" CTA (via `isConsentBlockingOrder`
 * feeding `isRecapStepValid`); the actual consent recording happens on the CTA itself. The label text
 * is deliberately NOT wired as the checkbox label so clicking the AGB / Datenschutz links (new tab)
 * cannot accidentally toggle the checkbox.
 *
 * Presentation only, so the legacy embed and PF v2 show the same control against
 * their own consent state rather than keeping two copies of the wording and the
 * link handling.
 *
 * @example <EmbedConsentCheckbox checked={isConsentChecked} onChange={setIsConsentChecked} />
 */
interface Props {
  checked: boolean
  onChange: (checked: boolean) => void
}

export const EmbedConsentCheckbox: FC<Props> = ({ checked, onChange }) => {
  const { t } = useTranslation(['embed'])

  const termsLink = useMemo(() => getTermsLink(i18n.language), [])
  const privacyPolicyLink = useMemo(() => getPrivacyPolicyLink(i18n.language), [])

  // The raw translation carries <termsLink>/<privacyLink> markup for Trans — strip it for the aria label.
  const ariaLabel = t('consent.checkbox_label').replace(/<[^>]+>/g, '')

  return (
    <BorderBoxWrapper padding={2} data-testid="embed-consent-checkbox">
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <MUICheckbox
          checked={checked}
          onClick={() => onChange(!checked)}
          inputProps={{ 'aria-label': ariaLabel }}
        />
        <Typography variant="text-sm" color={GRAY_700}>
          <Trans
            t={t}
            i18nKey="consent.checkbox_label"
            components={{
              termsLink: <Link href={termsLink} rel="noopener noreferrer" target="_blank" sx={linkStyle} />,
              privacyLink: <Link href={privacyPolicyLink} rel="noopener noreferrer" target="_blank" sx={linkStyle} />,
            }}
          />
        </Typography>
      </Stack>
    </BorderBoxWrapper>
  )
}
